import React, { forwardRef, useState, useEffect, useRef, useMemo } from "react";
import { 
    Box, TextField, Popper, Paper, List, ListItemButton, ListItemText, 
    Typography, ClickAwayListener, IconButton, Dialog, AppBar, Toolbar, Slide, useTheme, useMediaQuery
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { TransitionProps } from '@mui/material/transitions';
import iconMap from "../../../paginas/gestao/menu/iconmap";
import tokenService from "../../../services/tokenservice";
import { useEasyBrowserContext } from "../browser";
import { MenuItemType } from "../taskbar/taskbartypes.d";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const normalize = (text: string) => 
    (text || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const FindTaskBar = forwardRef((_props: unknown, _ref: React.ForwardedRef<any>) => {
    const { abrirPagina, usuario } = useEasyBrowserContext()!;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [allScreens, setAllScreens] = useState<MenuItemType[]>([]);
    const [history, setHistory] = useState<MenuItemType[]>([]);
    const [open, setOpen] = useState(false);
    const [openMobile, setOpenMobile] = useState(false);
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const containerRef = useRef<HTMLDivElement | null>(null);

    const HISTORY_KEY = `find_history_${usuario?.login || 'default'}`;

    useEffect(() => {
        const init = async () => {
            const menu = await tokenService.getMenu();
            if (menu) {
                try {
                    const parsed = JSON.parse(menu);
                    setAllScreens(parsed.menus || []);
                } catch (e) { console.error(e); }
            }
            const saved = localStorage.getItem(HISTORY_KEY);
            if (saved) setHistory(JSON.parse(saved));
        };
        init();
    }, [usuario?.login, HISTORY_KEY]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), 150);
        return () => clearTimeout(timer);
    }, [query]);

    // Lógica de busca focada na DESCRIÇÃO (Nome da Tela)
    const filteredResults = useMemo(() => {
        const termo = normalize(debouncedQuery);
        if (!termo) return [];

        return allScreens
            .filter(item => {
                const desc = normalize(item.descricao);
                const grupo = normalize(item.grupo?.join(" ") || "");
                // Busca primária na descrição, secundária no grupo
                return desc.includes(termo) || grupo.includes(termo);
            })
            .sort((a, b) => {
                const descA = normalize(a.descricao);
                const descB = normalize(b.descricao);
                
                // Prioridade 1: Começa com o termo
                const startsA = descA.startsWith(termo);
                const startsB = descB.startsWith(termo);
                if (startsA && !startsB) return -1;
                if (!startsA && startsB) return 1;

                // Prioridade 2: Ordem alfabética da descrição
                return descA.localeCompare(descB);
            })
            .slice(0, 12); 
    }, [debouncedQuery, allScreens]);

    const selecionar = (item: MenuItemType) => {
        const newHistory = [item, ...history.filter(h => h.id !== item.id)].slice(0, 5);
        setHistory(newHistory);
        localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));

        abrirPagina(item);
        setQuery("");
        setOpen(false);
        setOpenMobile(false);
    };

    // Atalho de teclado Alt+Q
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.altKey && e.key.toLowerCase() === 'q') {
                e.preventDefault();
                setOpen(true);
                // O foco automático será tratado pelo TextField onFocus
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const renderLista = (itens: MenuItemType[], isHistory = false) => (
        <List dense={!isMobile} sx={{ py: 0 }}>
            {isHistory && itens.length > 0 && (
                <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 700, color: 'text.secondary', bgcolor: 'action.hover', letterSpacing: 1 }}>
                    RECENTES
                </Typography>
            )}
            {itens.map((item) => (
                <ListItemButton key={item.id} onClick={() => selecionar(item)} sx={{ py: 1 }}>
                    <Box sx={{ mr: 2, display: 'flex', color: 'primary.main', opacity: 0.8 }}>
                        {isHistory ? <HistoryIcon fontSize="small" /> : (iconMap[item.icone || "default"] || iconMap["default"])}
                    </Box>
                    <ListItemText 
                        primary={item.descricao} 
                        secondary={item.grupo?.join(" > ")}
                        primaryTypographyProps={{ 
                            fontSize: isMobile ? '1rem' : '0.9rem', 
                            fontWeight: 600,
                            color: 'text.primary' 
                        }}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                </ListItemButton>
            ))}
        </List>
    );

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box ref={containerRef} sx={{ position: 'relative' }}>
                {/* Desktop Search Bar */}
                {!isMobile && (
                    <Box sx={{ 
                        width: 240, bgcolor: 'background.paper', borderRadius: '8px', px: 1.5, display: 'flex', alignItems: 'center', 
                        border: '1px solid', borderColor: open ? 'primary.main' : 'divider', boxShadow: open ? 2 : 0, transition: '0.2s'
                    }}>
                        <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                        <TextField
                            placeholder="Buscar tela... (Alt+Q)"
                            variant="standard"
                            fullWidth
                            autoComplete="off"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onFocus={() => setOpen(true)}
                            InputProps={{ disableUnderline: true, sx: { fontSize: 13, height: 36 } }}
                        />
                    </Box>
                )}

                {/* Mobile Icon */}
                {isMobile && (
                    <IconButton onClick={() => setOpenMobile(true)} color="inherit"><SearchIcon /></IconButton>
                )}

                {/* Desktop Results Dropdown */}
                <Popper open={open && !isMobile} anchorEl={containerRef.current} placement="bottom-start" style={{ zIndex: 3000, width: 350 }}>
                    <Paper elevation={8} sx={{ mt: 1, maxHeight: 450, overflow: 'auto', borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}>
                        {query.trim() === "" ? renderLista(history, true) : renderLista(filteredResults)}
                        {query && filteredResults.length === 0 && (
                            <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">Nenhuma tela encontrada</Typography></Box>
                        )}
                    </Paper>
                </Popper>

                {/* Mobile Fullscreen Search */}
                <Dialog fullScreen open={openMobile} onClose={() => setOpenMobile(false)} TransitionComponent={Transition}>
                    <AppBar sx={{ position: 'relative', bgcolor: 'background.paper', color: 'text.primary', elevation: 1 }}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={() => setOpenMobile(false)}><CloseIcon /></IconButton>
                            <TextField
                                autoFocus
                                fullWidth
                                placeholder="Nome da tela..."
                                variant="standard"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                InputProps={{ disableUnderline: true, sx: { ml: 2, fontSize: '1.1rem' } }}
                            />
                        </Toolbar>
                    </AppBar>
                    <Box sx={{ bgcolor: 'background.default', flex: 1, overflowY: 'auto' }}>
                        {query.trim() === "" ? renderLista(history, true) : renderLista(filteredResults)}
                    </Box>
                </Dialog>
            </Box>
        </ClickAwayListener>
    );
});

export default FindTaskBar;